const RELOAD_SESSION_KEY = "app-version-reload";
const CHECK_INTERVAL_MS = 60_000;

export type AppVersionPayload = {
  version: string;
};

export const getCurrentAppVersion = (): string | undefined => {
  const version = import.meta.env.VITE_APP_VERSION;
  return typeof version === "string" && version.length > 0
    ? version
    : undefined;
};

export const shouldReloadForVersion = (
  currentVersion: string | undefined,
  remoteVersion: string | undefined,
  alreadyReloadedFor: string | null,
): boolean => {
  if (!currentVersion || !remoteVersion) return false;
  if (remoteVersion === currentVersion) return false;
  // Avoid reload loops when HTML is still briefly served from an older CDN edge.
  if (alreadyReloadedFor === remoteVersion) return false;
  return true;
};

export const fetchRemoteAppVersion = async (
  fetchImpl: typeof fetch = fetch,
): Promise<string | undefined> => {
  const response = await fetchImpl(`/version.json?t=${Date.now()}`, {
    cache: "no-store",
  });
  if (!response.ok) return undefined;
  const payload = (await response.json()) as AppVersionPayload;
  return typeof payload.version === "string" && payload.version.length > 0
    ? payload.version
    : undefined;
};

export const startAppVersionWatcher = (
  options: {
    intervalMs?: number;
    reload?: () => void;
    fetchImpl?: typeof fetch;
  } = {},
): (() => void) | undefined => {
  if (import.meta.env.DEV) return undefined;

  const currentVersion = getCurrentAppVersion();
  if (!currentVersion) return undefined;

  const reload = options.reload ?? (() => window.location.reload());
  const fetchImpl = options.fetchImpl ?? fetch;
  const intervalMs = options.intervalMs ?? CHECK_INTERVAL_MS;
  let disposed = false;

  const check = async () => {
    if (disposed) return;
    try {
      const remoteVersion = await fetchRemoteAppVersion(fetchImpl);
      if (disposed) return;

      if (remoteVersion === currentVersion) {
        sessionStorage.removeItem(RELOAD_SESSION_KEY);
        return;
      }

      const alreadyReloadedFor = sessionStorage.getItem(RELOAD_SESSION_KEY);
      if (
        !shouldReloadForVersion(
          currentVersion,
          remoteVersion,
          alreadyReloadedFor,
        )
      ) {
        return;
      }

      sessionStorage.setItem(RELOAD_SESSION_KEY, remoteVersion!);
      reload();
    } catch {
      // Ignore transient network / parse errors.
    }
  };

  const intervalId = window.setInterval(() => {
    void check();
  }, intervalMs);

  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      void check();
    }
  };
  document.addEventListener("visibilitychange", onVisibilityChange);

  void check();

  return () => {
    disposed = true;
    window.clearInterval(intervalId);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
};
